import { apiFetch } from "../api/client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import API from "../config";

function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function statusClass(status) {
  return `compliance-control-status compliance-control-status-${String(
    status || "not_assessed"
  ).replaceAll("_", "-")}`;
}

function scoreClass(score) {
  const numericScore = Number(score || 0);

  if (numericScore >= 90) {
    return "compliance-score compliance-score-excellent";
  }

  if (numericScore >= 75) {
    return "compliance-score compliance-score-good";
  }

  if (numericScore >= 50) {
    return "compliance-score compliance-score-partial";
  }

  return "compliance-score compliance-score-low";
}

function ComplianceAssessmentPage() {
  const [ruleEvaluations, setRuleEvaluations] =
    useState([]);
  const [evaluatingRules, setEvaluatingRules] =
    useState(false);
  const [ruleEvaluationMessage, setRuleEvaluationMessage] =
    useState("");

  const { assessmentId } = useParams();

  const [assessment, setAssessment] = useState(null);
  const [evidence, setEvidence] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [savingControlId, setSavingControlId] =
    useState(null);

  const [error, setError] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadAssessment = useCallback(
    async (silent = false) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const [assessmentResponse, evidenceResponse] =
          await Promise.all([
            apiFetch(`/compliance/assessments/${assessmentId}`
            ),
            apiFetch(`/compliance/assessments/${assessmentId}/evidence`
            ),
          ]);

        const assessmentData =
          await assessmentResponse.json();

        const evidenceData =
          await evidenceResponse.json();

        if (!assessmentResponse.ok) {
          throw new Error(
            assessmentData.detail ||
              "Unable to load assessment."
          );
        }

        if (!evidenceResponse.ok) {
          throw new Error(
            evidenceData.detail ||
              "Unable to load compliance evidence."
          );
        }

        setAssessment(assessmentData);

        setEvidence(
          Array.isArray(evidenceData)
            ? evidenceData
            : []
        );
      } catch (loadError) {
        console.error(
          "Compliance assessment load error:",
          loadError
        );

        setError(loadError.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [assessmentId]
  );

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  const loadRuleEvaluations = useCallback(async () => {
    try {
      const response = await apiFetch(
        `/compliance/assessments/${assessmentId}/rule-evaluations`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load compliance rule evaluations."
        );
      }

      setRuleEvaluations(
        Array.isArray(data) ? data : []
      );
    } catch (evaluationLoadError) {
      console.error(
        "Rule evaluation history error:",
        evaluationLoadError
      );
    }
  }, [assessmentId]);

  useEffect(() => {
    loadRuleEvaluations();
  }, [loadRuleEvaluations]);

  const evaluateRules = async () => {
    try {
      setEvaluatingRules(true);
      setRuleEvaluationMessage("");

      const response = await apiFetch(
        `/compliance/assessments/${assessmentId}/evaluate-rules`,
        {
          method: "POST",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to evaluate compliance rules."
        );
      }

      setRuleEvaluationMessage(
        `${data.rules_evaluated || 0} compliance rule(s) evaluated.`
      );

      await Promise.all([
        loadAssessment(true),
        loadRuleEvaluations(),
      ]);
    } catch (evaluationError) {
      console.error(
        "Compliance rule evaluation error:",
        evaluationError
      );

      setRuleEvaluationMessage(
        evaluationError.message
      );
    } finally {
      setEvaluatingRules(false);
    }
  };

  const collectEvidence = async () => {
    try {
      setCollecting(true);

      const response = await apiFetch(`/compliance/assessments/${assessmentId}/collect-evidence`,
        {
          method: "POST",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to collect automated evidence."
        );
      }

      await loadAssessment(true);

      window.alert(
        `${data.controls_evaluated || 0} control(s) evaluated.`
      );
    } catch (collectError) {
      console.error(
        "Evidence collection error:",
        collectError
      );

      window.alert(collectError.message);
    } finally {
      setCollecting(false);
    }
  };

  const updateControl = async (
    control,
    field,
    value
  ) => {
    try {
      setSavingControlId(control.control_id);

      const status =
        field === "status"
          ? value
          : control.status || "not_assessed";

      const payload = {
        status,
        score:
          field === "score"
            ? Number(value)
            : Number(control.score || 0),
        implementation_status:
          control.implementation_status || status,
        finding_summary:
          control.finding_summary || "",
        analyst_notes:
          control.analyst_notes || "",
        assessed_by:
          localStorage.getItem("username") ||
          "Authenticated Administrator",
        assessed_by_role:
          localStorage.getItem("role") ||
          "authenticated_user",
        remediation_required:
          field === "remediation_required"
            ? Boolean(value)
            : Boolean(control.remediation_required),
      };

      const response = await apiFetch(`/compliance/assessments/${assessmentId}/controls/${control.control_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to update compliance control."
        );
      }

      await loadAssessment(true);
    } catch (updateError) {
      console.error(
        "Compliance control update error:",
        updateError
      );

      window.alert(updateError.message);
    } finally {
      setSavingControlId(null);
    }
  };

  const controls = Array.isArray(assessment?.controls)
    ? assessment.controls
    : [];

  const families = useMemo(() => {
    return [
      ...new Set(
        controls
          .map((control) => control.control_family)
          .filter(Boolean)
      ),
    ].sort();
  }, [controls]);

  const filteredControls = useMemo(() => {
    return controls.filter((control) => {
      const familyMatch =
        familyFilter === "all" ||
        control.control_family === familyFilter;

      const statusMatch =
        statusFilter === "all" ||
        control.status === statusFilter;

      return familyMatch && statusMatch;
    });
  }, [controls, familyFilter, statusFilter]);

  const evidenceByControl = useMemo(() => {
    const grouped = {};

    evidence.forEach((item) => {
      const key = String(item.control_id);

      if (!grouped[key]) {
        grouped[key] = [];
      }

      grouped[key].push(item);
    });

    return grouped;
  }, [evidence]);

  if (loading) {
    return (
      <div className="compliance-loading">
        Loading compliance assessment...
      </div>
    );
  }

  if (error && !assessment) {
    return (
      <div className="compliance-assessment-error">
        <h3>Assessment unavailable</h3>
        <p>{error}</p>

        <button
          type="button"
          onClick={() => loadAssessment()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="compliance-assessment-page">
      <section className="compliance-assessment-header">
        <div>
          <Link to="/compliance-center">
            ← Back to Compliance Center
          </Link>

          <div className="compliance-eyebrow">
            {assessment.framework_name}
          </div>

          <h2>{assessment.assessment_name}</h2>

          <p>
            {assessment.scope_description ||
              "Continuous compliance assessment"}
          </p>
        </div>

        <div className="compliance-assessment-header-actions">
          <button
            type="button"
            onClick={() => loadAssessment(true)}
            disabled={refreshing}
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh"}
          </button>

          <button
            type="button"
            className="compliance-collect-button"
            onClick={evaluateRules}
            disabled={evaluatingRules}
          >
            {evaluatingRules
              ? "Evaluating Rules..."
              : "Evaluate Rules"}
          </button>

          <button
            type="button"
            className="compliance-collect-button"
            onClick={collectEvidence}
            disabled={collecting}
          >
            {collecting
              ? "Collecting Evidence..."
              : "Collect Automated Evidence"}
          </button>
        </div>
      </section>

      {error && (
        <div className="compliance-warning">
          Latest refresh failed: {error}
        </div>
      )}

      <section className="compliance-assessment-metrics">
        <article>
          <span>Overall Score</span>
          <strong
            className={scoreClass(
              assessment.overall_score
            )}
          >
            {Number(
              assessment.overall_score || 0
            ).toFixed(1)}
            %
          </strong>
        </article>

        <article>
          <span>Maturity</span>
          <strong>
            {formatLabel(
              assessment.maturity_level
            )}
          </strong>
        </article>

        <article>
          <span>Controls</span>
          <strong>{controls.length}</strong>
        </article>

        <article>
          <span>Evidence Records</span>
          <strong>{evidence.length}</strong>
        </article>
      </section>

      <section className="compliance-family-grid">
        {(assessment.family_scores || []).map(
          (family) => (
            <article key={family.control_family}>
              <span>{family.control_family}</span>

              <strong>
                {Number(family.score || 0).toFixed(1)}%
              </strong>

              <small>
                {family.control_count} controls
              </small>
            </article>
          )
        )}
      </section>

      <section className="compliance-assessment-filters">
        <select
          value={familyFilter}
          onChange={(event) =>
            setFamilyFilter(event.target.value)
          }
        >
          <option value="all">All families</option>

          {families.map((family) => (
            <option key={family} value={family}>
              {family}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(event.target.value)
          }
        >
          <option value="all">All statuses</option>
          <option value="implemented">
            Implemented
          </option>
          <option value="partially_implemented">
            Partially Implemented
          </option>
          <option value="planned">Planned</option>
          <option value="not_implemented">
            Not Implemented
          </option>
          <option value="not_assessed">
            Not Assessed
          </option>
          <option value="not_applicable">
            Not Applicable
          </option>
        </select>
      </section>

      <section className="compliance-control-list">
        {filteredControls.map((control) => {
          const controlEvidence =
            evidenceByControl[
              String(control.control_id)
            ] || [];

          const saving =
            savingControlId === control.control_id;

          return (
            <article
              className="compliance-control-card"
              key={control.id}
            >
              <div className="compliance-control-card-header">
                <div>
                  <span className="compliance-control-id">
                    {control.control_identifier}
                  </span>

                  <h3>{control.control_title}</h3>

                  <p>
                    {control.control_family}
                    {" · "}
                    {control.category}
                  </p>
                </div>

                <div className="compliance-control-card-score">
                  <span
                    className={statusClass(
                      control.status
                    )}
                  >
                    {formatLabel(control.status)}
                  </span>

                  <strong>
                    {Number(control.score || 0).toFixed(1)}%
                  </strong>
                </div>
              </div>

              <p className="compliance-control-description">
                {control.description}
              </p>

              <div className="compliance-control-guidance">
                <strong>Implementation guidance</strong>
                <span>
                  {control.implementation_guidance ||
                    "No implementation guidance provided."}
                </span>
              </div>

              <div className="compliance-control-editor">
                <label>
                  Status
                  <select
                    value={
                      control.status || "not_assessed"
                    }
                    disabled={saving}
                    onChange={(event) =>
                      updateControl(
                        control,
                        "status",
                        event.target.value
                      )
                    }
                  >
                    <option value="implemented">
                      Implemented
                    </option>

                    <option value="partially_implemented">
                      Partially Implemented
                    </option>

                    <option value="planned">
                      Planned
                    </option>

                    <option value="not_implemented">
                      Not Implemented
                    </option>

                    <option value="not_assessed">
                      Not Assessed
                    </option>

                    <option value="not_applicable">
                      Not Applicable
                    </option>
                  </select>
                </label>

                <label>
                  Score
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={Number(
                      control.score || 0
                    )}
                    disabled={saving}
                    onChange={(event) =>
                      updateControl(
                        control,
                        "score",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="compliance-remediation-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(
                      control.remediation_required
                    )}
                    disabled={saving}
                    onChange={(event) =>
                      updateControl(
                        control,
                        "remediation_required",
                        event.target.checked
                      )
                    }
                  />

                  Remediation required
                </label>
              </div>

              <div className="compliance-control-findings">
                <div>
                  <span>Implementation</span>

                  <strong>
                    {formatLabel(
                      control.implementation_status
                    )}
                  </strong>
                </div>

                <div>
                  <span>Evidence</span>

                  <strong>
                    {control.evidence_count || 0}
                  </strong>
                </div>

                <div>
                  <span>Assessed By</span>

                  <strong>
                    {control.assessed_by || "Not assessed"}
                  </strong>
                </div>

                <div>
                  <span>Assessed</span>

                  <strong>
                    {formatDate(control.assessed_at)}
                  </strong>
                </div>
              </div>

              {control.finding_summary && (
                <div className="compliance-control-summary">
                  <strong>Finding summary</strong>
                  <p>{control.finding_summary}</p>
                </div>
              )}

              <div className="compliance-evidence-list">
                <div className="compliance-evidence-heading">
                  <strong>Evidence</strong>
                  <span>
                    {controlEvidence.length} record(s)
                  </span>
                </div>

                {controlEvidence.length > 0 ? (
                  controlEvidence.map((item) => (
                    <div
                      className="compliance-evidence-item"
                      key={item.id}
                    >
                      <div>
                        <strong>{item.title}</strong>

                        <span>
                          {item.description}
                        </span>
                      </div>

                      <div>
                        <span>
                          {formatLabel(
                            item.source_record_type
                          )}
                        </span>

                        <small>
                          {formatDate(
                            item.collected_at
                          )}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="compliance-evidence-empty">
                    No evidence collected for this control.
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>
      <section className="compliance-panel compliance-rule-evaluation-history">
        <div className="compliance-panel-heading">
          <div>
            <span>AUTOMATED RULE ENGINE</span>
            <h3>Rule Evaluation Results</h3>
          </div>

          <strong>
            {ruleEvaluations.length} evaluations
          </strong>
        </div>

        {ruleEvaluationMessage && (
          <div className="compliance-warning">
            {ruleEvaluationMessage}
          </div>
        )}

        <div className="compliance-table-wrapper">
          <table className="compliance-table">
            <thead>
              <tr>
                <th>Control</th>
                <th>Rule</th>
                <th>Status</th>
                <th>Score</th>
                <th>Message</th>
                <th>Evaluated</th>
              </tr>
            </thead>

            <tbody>
              {ruleEvaluations.length > 0 ? (
                ruleEvaluations.map((evaluation) => (
                  <tr key={evaluation.id}>
                    <td>
                      {evaluation.control_identifier}
                    </td>

                    <td>
                      <strong>
                        {evaluation.rule_name}
                      </strong>
                    </td>

                    <td>
                      {formatLabel(
                        evaluation.evaluation_status
                      )}
                    </td>

                    <td>
                      {Number(
                        evaluation.score || 0
                      ).toFixed(1)}
                      %
                    </td>

                    <td>
                      {evaluation.message || "—"}
                    </td>

                    <td>
                      {formatDate(
                        evaluation.evaluated_at
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    No rule evaluations recorded yet.
                    Select Evaluate Rules to run the
                    verified compliance rule engine.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}

export default ComplianceAssessmentPage;
